import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, TextField } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import config from '../../../../config';
import { convertFileName, simplifyJson } from 'Utils/flowExport';
import { copyText } from 'Utils/clipboard';

const DEBUG_MARKER = 'Flow execution has been interrupted with the showRequest function';

function parseDebugResponse(responseStr) {
    try {
        const data = JSON.parse(responseStr);
        if (data.error && typeof data.error === 'string' && data.error.includes(DEBUG_MARKER)) {
            return {
                isDebug: true,
                message: data.message,
                debugMethod: data._debug_method || null,
                debugField: data._debug_field || null,
            };
        }
    } catch {}
    return { isDebug: false };
}

function BeautifyJson({ jsonStr, isError, isDebug, simpleVersion }) {
    const processedStr = simpleVersion ? simplifyJson(jsonStr) : jsonStr;
    const beautifiedJson = JSON.stringify(JSON.parse(processedStr), null, 4);

    const bgColor = isDebug ? '#fff8e1' : isError ? '#ffebee' : '#f5f5f5';
    const textColor = isDebug ? '#f57f17' : isError ? '#c62828' : 'inherit';

    return (
        <pre style={{
            overflow: 'auto',
            maxHeight: '60vh',
            backgroundColor: bgColor,
            padding: '16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'monospace',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: textColor,
        }}>{beautifiedJson}</pre>
    );
}
const FlowImportFlow = ({name, title, visible, setVisible, response, setResponse, status, setStatus, flowId, simpleVersion, setSimpleVersion, includeSubflows, setIncludeSubflows, isBackground, onGetResult, onBackgroundReset, onStop}) => {
    const { enqueueSnackbar } = useSnackbar();
    const hiddenTextareaRef = useRef(null);
    const [fetching, setFetching] = useState(false);
    const [autoPoll, setAutoPoll] = useState(true);
    const [pollSeconds, setPollSeconds] = useState(60);
    const fetchingRef = useRef(false);

    const isError = status !== null && (status < 200 || status >= 300);
    const debugInfo = parseDebugResponse(response);
    const isDebug = debugInfo.isDebug;

    const onClose = () => {
        setAutoPoll(true);
        setVisible(false);
        setResponse('');
        if (setStatus) {
            setStatus(null);
        }
        if (onBackgroundReset) {
            onBackgroundReset();
        }
    };

    const handleGetResult = async () => {
        if (!onGetResult || fetchingRef.current) {
            return;
        }
        fetchingRef.current = true;
        setFetching(true);
        try {
            await onGetResult();
        } finally {
            fetchingRef.current = false;
            setFetching(false);
        }
    };

    const getResultRef = useRef(handleGetResult);
    getResultRef.current = handleGetResult;
    const pollIntervalMs = Math.max(10, Number(pollSeconds) || 60) * 1000;

    const polling = Boolean(visible && autoPoll && isBackground && onGetResult);
    const [nextCheckAt, setNextCheckAt] = useState(null);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!polling) {
            setNextCheckAt(null);
            return undefined;
        }
        setNextCheckAt(Date.now() + pollIntervalMs);
        const timer = setInterval(() => {
            setNextCheckAt(Date.now() + pollIntervalMs);
            getResultRef.current();
        }, pollIntervalMs);
        const clock = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            clearInterval(timer);
            clearInterval(clock);
        };
    }, [polling, pollIntervalMs]);

    const secondsLeft = nextCheckAt === null ? 0 : Math.max(0, Math.ceil((nextCheckAt - now) / 1000));

    const execCopy = (text, successMessage, successCallback) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                enqueueSnackbar(successMessage || 'Copied to clipboard.', {
                    action: CustomSnackbar,
                    variant: 'success'
                });
                if (successCallback) successCallback();
            } else {
                enqueueSnackbar('Failed to copy to clipboard', {
                    action: CustomSnackbar,
                    variant: 'error'
                });
            }
        } catch (err) {
            enqueueSnackbar('Failed to copy to clipboard', {
                action: CustomSnackbar,
                variant: 'error'
            });
        }

        document.body.removeChild(textArea);
    };

    const copyForAI = async () => {
        if (!response || !flowId) return;

        try {
            const exportRes = await http.get(`/flow/${flowId}/export`);
            const flowJson = JSON.stringify(exportRes.data, null, 2);
            const errorJson = JSON.stringify(JSON.parse(response), null, 2);

            const promptRes = await http.post('/ai/generate-runtime-error-prompt', {
                error: errorJson,
                flow_json: flowJson
            });

            const prompt = promptRes.data.prompt;

            // Write to hidden textarea
            if (hiddenTextareaRef.current) {
                hiddenTextareaRef.current.value = prompt;
            }

            copyText(prompt)
                .then(() => {
                    enqueueSnackbar('Prompt copied! Paste it into ChatGPT or Claude.', {
                        action: CustomSnackbar,
                        variant: 'success'
                    });
                })
                .catch(() => {
                    enqueueSnackbar('Failed to copy', {
                        action: CustomSnackbar,
                        variant: 'error'
                    });
                });
        } catch (err) {
            enqueueSnackbar('Unable to generate AI prompt', {
                action: CustomSnackbar,
                variant: 'error'
            });
        }
    };

    const copyToClipboard = () => {
        if (response) {
            const processedResponse = simpleVersion ? simplifyJson(response) : response;
            let text;
            try {
                text = JSON.stringify(JSON.parse(processedResponse), null, 4);
            } catch (err) {
                text = processedResponse;
            }
            copyText(text)
                .then(() => {
                    enqueueSnackbar('The response data has been copied to the clipboard.', {
                        action: CustomSnackbar,
                        variant: 'success'
                    });
                })
                .catch(() => {
                    enqueueSnackbar('Failed to copy to clipboard', {
                        action: CustomSnackbar,
                        variant: 'error'
                    });
                });
        }
    };

    const downloadFile = () => {
        if (response) {
            const processedResponse = simpleVersion ? simplifyJson(response) : response;
            const fileData = JSON.stringify(JSON.parse(processedResponse), null, 4);
            const blob = new Blob([fileData], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = convertFileName(name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
    };

    return (
        <>
        <Dialog
            open={visible}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{
                '& .MuiDialog-paper': {
                    width: '960px',
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    margin: '16px',
                }
            }}
        >
            <Box>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '8px',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box component="span" sx={{ color: '#3B465C', fontWeight: 600 }}>
                            {title}
                        </Box>
                        {status !== null && status !== undefined && (
                            <Chip
                                label={isDebug ? 'Debug' : `Status: ${status}`}
                                color={isDebug ? 'warning' : isError ? 'error' : 'success'}
                                size="small"
                            />
                        )}
                    </Box>
                    {title === 'Export Flow' && setSimpleVersion && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={simpleVersion}
                                    onChange={(e) => setSimpleVersion(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Simple version"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '14px',
                                    color: '#3B465C',
                                },
                            }}
                        />
                    )}
                    {title === 'Export Flow' && setIncludeSubflows && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!includeSubflows}
                                    onChange={(e) => setIncludeSubflows(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Include subflows"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '14px',
                                    color: '#3B465C',
                                },
                            }}
                        />
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button onClick={downloadFile} variant="outlined" size="small">
                            Download
                        </Button>
                        <Button onClick={copyToClipboard} variant="outlined" size="small">
                            Copy
                        </Button>
                        <Button onClick={onClose} variant="outlined" size="small">
                            Close
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {response && isDebug ? (
                        <Box
                            sx={{
                                backgroundColor: '#fff8e1',
                                border: '1px solid #ffe082',
                                borderRadius: '4px',
                                padding: '12px',
                                color: '#e65100',
                            }}
                        >
                            <Box sx={{ mb: 1 }}>
                                <strong>Debug response:</strong>
                                {debugInfo.debugMethod && (
                                    <span style={{ marginLeft: '8px', fontWeight: 400 }}>
                                        Method: <strong>{debugInfo.debugMethod}</strong>
                                        {debugInfo.debugField && <>, Field: <strong>{debugInfo.debugField}</strong></>}
                                    </span>
                                )}
                            </Box>
                            <BeautifyJson jsonStr={JSON.stringify(debugInfo.message)} isError={false} isDebug={true} simpleVersion={false} />
                        </Box>
                    ) : response && isError ? (
                        <Box
                            sx={{
                                backgroundColor: '#fdecea',
                                border: '1px solid #f5c2c0',
                                borderRadius: '4px',
                                padding: '12px',
                                color: '#b3261e',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <strong>Error response:</strong>
                                {config.AI_ENABLED && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={copyForAI}
                                    >
                                        Copy for AI
                                    </Button>
                                )}
                            </Box>
                            <BeautifyJson jsonStr={response} isError={isError} isDebug={false} simpleVersion={simpleVersion} />
                        </Box>
                    ) : response && (
                        <BeautifyJson jsonStr={response} isError={isError} isDebug={false} simpleVersion={simpleVersion} />
                    )}
                    {isBackground && (
                        <Box sx={{ mt: 2, color: '#3A4659', fontSize: '16px', lineHeight: 1.6 }}>
                            <Box>
                                The execution is continuing in the background.
                                {onGetResult && (
                                    <> While &ldquo;Auto-refresh&rdquo; is ticked, the result is checked automatically and appears here as soon as it is ready.
                                    You can also click &ldquo;Get Result&rdquo; to check right now.</>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 2 }}>
                                {onGetResult ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FormControlLabel
                                            sx={{ mr: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { color: '#3A4659' } }}
                                            control={<Checkbox size="small" checked={autoPoll} onChange={(e) => setAutoPoll(e.target.checked)} />}
                                            label="Auto-refresh every"
                                        />
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={pollSeconds}
                                            onChange={(e) => setPollSeconds(e.target.value)}
                                            inputProps={{ min: 10, 'aria-label': 'Auto-refresh interval, seconds' }}
                                            sx={{ width: 72 }}
                                        />
                                        <Box sx={{ whiteSpace: 'nowrap' }}>seconds</Box>
                                        {polling && (
                                            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, whiteSpace: 'nowrap' }}>
                                                <CircularProgress size={18} />
                                                {fetching ? 'Checking for the result…' : `Waiting for the result, next check in ${secondsLeft} s`}
                                            </Box>
                                        )}
                                    </Box>
                                ) : <Box />}
                                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                    {onStop && (
                                        <Button onClick={onStop} variant="contained" color="error" size="small" sx={{ whiteSpace: 'nowrap' }}>
                                            Stop
                                        </Button>
                                    )}
                                    {onGetResult && (
                                        <Button onClick={handleGetResult} variant="contained" size="small" disabled={fetching} sx={{ whiteSpace: 'nowrap' }}>
                                            {fetching ? 'Checking…' : 'Get Result'}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Box>
        </Dialog>
        <textarea
            ref={hiddenTextareaRef}
            style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
            tabIndex={-1}
            aria-hidden="true"
        />
        </>
    );
};

export default FlowImportFlow;
